import { FC, useEffect, useRef, useState } from 'react';
import SignaturePad from '../signaturePad/signaturePad';
import { clearSignature } from '../../utils/helper';
import { SignaturePadRef } from '../signaturePad/signaturePad.type';
import { Trans } from 'react-i18next';
import Button from '../button/button';
import { GetUsersResponse, ReportSignature } from '@inplay/models';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import close from '../../assets/close.png';

type SignatureModalProps = {
  userDetail?: GetUsersResponse;
  isOpen: boolean;
  onClose: () => void;
  handleAddSignature: (data: ReportSignature) => void;
  loading: boolean;
  editingSignature?: ReportSignature | null;
  isReviewing: boolean;
};

const SignatureModal: FC<SignatureModalProps> = (props) => {
  const sigCanvasRef = useRef<SignaturePadRef>(null);
  const { isTablet } = useResponsiveLayout();
  const [signatureName, setSignatureName] = useState<string>('');
  const [signature, setSignature] = useState<string | undefined>(undefined);
  const [hasDrawnNew, setHasDrawnNew] = useState(false);

  useEffect(() => {
    if (!props.userDetail) return;

    if (!props.isOpen) return;

    if (props.editingSignature) {
      setSignatureName(
        `${props.editingSignature.firstName} ${props.editingSignature.lastName}`.trim(),
      );
      setSignature(props.editingSignature.signature);
      setHasDrawnNew(false);
      if (sigCanvasRef.current) {
        sigCanvasRef.current.clear(); // Clear canvas when editing existing signature (show image instead)
      }
    } else {
      const userFullName = `${props.userDetail.firstName ?? ''} ${props.userDetail.lastName ?? ''}`;
      setSignatureName(userFullName.trim());
      setSignature(props.userDetail.signature);
      setHasDrawnNew(false);
      if (sigCanvasRef.current) {
        sigCanvasRef.current.clear();
      }
    }
  }, [props.isOpen, props.userDetail, props.editingSignature]);

  if (!props.isOpen) return null;

  const handleDeleteSignature = () => {
    clearSignature(sigCanvasRef);
    setSignature(undefined);
    setHasDrawnNew(false);
  };

  const handleDrawEnd = () => {
    const dataUrl = sigCanvasRef.current?.getTrimmedDataURL();
    if (dataUrl) {
      setSignature(dataUrl);
      setHasDrawnNew(true);
    }
  };

  const handleSave = () => {
    let firstName = signatureName.split(' ')[0] || '';
    let lastName = signatureName.split(' ')[1] || '';
    let signatureId = '';

    if (props.userDetail) {
      firstName = props.userDetail.firstName;
      lastName = props.userDetail.lastName;
      signatureId = `users/signature_${props.userDetail.id}.jpeg`;
    }

    props.handleAddSignature({
      firstName,
      lastName,
      signature: signature ?? '',
      signatureId: props.editingSignature?.signatureId ?? signatureId,
      signedDate: new Date(),
      notes: props.editingSignature?.notes,
      userId: props.userDetail?.id ?? '',
    });
  };

  const isSaveDisabled: boolean = !signature || !signatureName.trim();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-xl p-6 pb-0 shadow-lg relative overflow-auto ${
          isTablet
            ? 'h-[90vh] w-[85vw] flex flex-col pb-8'
            : 'w-full max-w-2xl h-[430px]'
        }`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            <Trans i18nKey="signature.add" />
          </h2>
          <img
            src={close}
            onClick={() => {
              setSignature(undefined);
              props.onClose();
            }}
            alt="Close"
            className="w-4 h-4 cursor-pointer"
          />
        </div>

        <label className="text-sm font-normal mb-1 block text-[#1A1A1A]">
          <Trans i18nKey="signature.hi" />{' '}
          <span className="font-semibold">
            {props.userDetail?.firstName} {props.userDetail?.lastName},
          </span>{' '}
          <Trans i18nKey="signature.pleaseAdd" />
          <br />
          <p className="mt-2">
            <Trans
              i18nKey="signature.acknowledgeSignature"
              values={{
                action: props.isReviewing ? 'reviewing' : 'creating',
              }}
            />
          </p>
        </label>

        <div className={`mt-5 ${isTablet && 'flex-grow'}`}>
          <SignaturePad
            ref={sigCanvasRef}
            onDelete={handleDeleteSignature}
            initialSignature={hasDrawnNew ? undefined : signature} // show image only if user hasn't drawn new signature
            handleDrawEnd={handleDrawEnd}
            style={isTablet ? { height: '100%' } : {}}
          />
        </div>

        <div className="flex justify-between items-center mt-4">
          <Button
            disabled={isSaveDisabled}
            className="w-[100%] py-2 rounded-full text-white border-[#CFCFCF]"
            onClick={handleSave}
          >
            {props.loading ? (
              <ButtonSpinner />
            ) : props.editingSignature ? (
              <Trans i18nKey="signature.update" />
            ) : (
              <Trans i18nKey="signature.save" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
